import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../config/api';
import AuthContext from '../context/AuthContext';
import { Loader2, AlertCircle, Calendar, MapPin, Code, IndianRupee, ArrowLeft, Users, Sparkles, CheckCircle } from 'lucide-react';

const InternshipDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated } = useContext(AuthContext);
    const [internship, setInternship] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchInternship = async () => {
            try {
                const { data } = await api.get(`/internships/public/${id}`);
                setInternship(data.data);
            } catch (err) {
                setError('Could not load internship details. It might be closed or invalid.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchInternship();
    }, [id]);

    const handleApplyClick = () => {
        if (!isAuthenticated) {
            navigate('/login', { state: { from: `/internships/${id}` } });
            return;
        }
        navigate(`/payment?internshipId=${id}`);
    };
    
    if (loading) return (
        <div className="flex justify-center items-center min-h-[60vh] text-slate-800">
            <div className="flex flex-col items-center gap-4">
                <Loader2 className="animate-spin h-12 w-12 text-indigo-600" />
                <p className="text-slate-500 font-semibold text-sm">Fetching internship details...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="flex flex-col justify-center items-center min-h-[60vh] text-center p-6 text-slate-800">
            <AlertCircle className="h-14 w-14 text-rose-500 mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Error Loading Program</h2>
            <p className="text-slate-500 mb-6 max-w-md">{error}</p>
            <Link to="/internships" className="inline-flex items-center px-5 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-sm">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Programs
            </Link>
        </div>
    );

    if (!internship) return null;

    const remainingPositions = typeof internship.totalPositions === 'number' 
        ? Math.max(0, internship.totalPositions - (internship.currentRegistrations || 0))
        : null;

    const isAvailable = internship.isAccepting && (remainingPositions === null || remainingPositions > 0);

    return (
        <div className="bg-slate-50 min-h-screen py-10 font-sans text-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Back Button */}
                <div className="mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 text-indigo-600 hover:text-indigo-800 font-bold text-sm shadow-sm transition-all active:scale-95"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Internships
                    </button>
                </div>

                {/* Hero / Cover Visual banner if image exists */}
                {internship.image && (
                    <div className="relative rounded-3xl overflow-hidden shadow-md mb-8 max-h-[320px] border border-slate-200">
                        <img src={internship.image} alt={internship.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                        {internship.tag && (
                            <span 
                                className="absolute top-4 right-4 px-4 py-2 rounded-full text-xs font-bold text-white shadow-md uppercase tracking-wider"
                                style={{ backgroundColor: internship.tagColor || '#4F46E5' }}
                            >
                                {internship.tag}
                            </span>
                        )}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    {/* Left Panel: 2/3 Content */}
                    <div className="lg:col-span-8 space-y-6">
                        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm">
                            <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 font-bold text-xs border border-indigo-100">
                                    <Sparkles className="w-3.5 h-3.5" /> Virtual Track
                                </span>
                                {!internship.image && internship.tag && (
                                    <span 
                                        className="px-3.5 py-1 rounded-full text-xs font-bold text-white shadow-sm uppercase tracking-wider"
                                        style={{ backgroundColor: internship.tagColor || '#4F46E5' }}
                                    >
                                        {internship.tag}
                                    </span>
                                )}
                            </div>
                            
                            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight leading-snug">
                                {internship.title}
                            </h1>
                            <p className="text-slate-500 text-base sm:text-lg mb-8 leading-relaxed">
                                {internship.shortDescription}
                            </p>

                            {/* Mobile only Stats preview */}
                            <div className="grid grid-cols-3 gap-2 sm:hidden mb-6">
                                <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center">
                                    <span className="block text-[9px] text-slate-400 font-bold uppercase mb-0.5">Stipend</span>
                                    <span className="font-bold text-slate-800 text-xs truncate block">₹{internship.stipend || 0}</span>
                                </div>
                                <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center">
                                    <span className="block text-[9px] text-slate-400 font-bold uppercase mb-0.5">Duration</span>
                                    <span className="font-semibold text-slate-800 text-xs truncate block">{internship.duration}</span>
                                </div>
                                <div className="bg-slate-50 border border-slate-100 rounded-xl p-3 text-center">
                                    <span className="block text-[9px] text-slate-400 font-bold uppercase mb-0.5">Location</span>
                                    <span className="font-semibold text-slate-800 text-xs truncate block">{internship.location}</span>
                                </div>
                            </div>

                            <div className="border-t border-slate-100 pt-8 space-y-8">
                                {/* Description */}
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-4">Program Description</h3>
                                    <div className="text-slate-600 leading-relaxed text-sm sm:text-base whitespace-pre-line">
                                        {internship.description}
                                    </div>
                                </div>

                                {/* Key Features list */}
                                {internship.features && internship.features.length > 0 && (
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900 mb-4">Key Benefits & Offerings</h3>
                                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {internship.features.map((feature, idx) => (
                                                <li key={idx} className="flex items-start gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100/60">
                                                    <CheckCircle className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" />
                                                    <span className="text-slate-600 text-sm leading-relaxed">{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Technologies */}
                                {internship.technologies && Array.isArray(internship.technologies) && internship.technologies.length > 0 && (
                                    <div>
                                         <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                                             <Code className="h-5 w-5 text-indigo-600" />
                                             Tools & Technologies Cover
                                         </h3>
                                         <div className="flex flex-wrap gap-2.5">
                                            {internship.technologies.map(tech => (
                                                <span key={tech} className="bg-slate-50 text-slate-600 text-xs font-bold px-3 py-1.5 rounded-xl border border-slate-200">{tech}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Right Panel: 1/3 Quick facts card */}
                    <div className="lg:col-span-4 space-y-6">
                        <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-sm space-y-6 lg:sticky lg:top-28">
                            <h3 className="text-lg font-bold text-slate-900 pb-3 border-b border-slate-100">Program Details</h3>
                            
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100">
                                        <IndianRupee className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Stipend</p>
                                        <p className="font-bold text-slate-900 text-sm">
                                            {internship.stipend > 0 ? `₹${internship.stipend} / ${internship.stipendType || 'month'}` : 'Unpaid'}
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-lg bg-blue-50 text-blue-600 border border-blue-100">
                                        <Calendar className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Duration</p>
                                        <p className="font-bold text-slate-900 text-sm">{internship.duration}</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div className="p-2.5 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-100">
                                        <MapPin className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Location / Format</p>
                                        <p className="font-bold text-slate-900 text-sm">{internship.location}</p>
                                    </div>
                                </div>

                                {remainingPositions !== null && (
                                    <div className="flex items-center gap-3">
                                        <div className="p-2.5 rounded-lg bg-amber-50 text-amber-600 border border-amber-100">
                                            <Users className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Positions Left</p>
                                            <p className="font-bold text-slate-900 text-sm">
                                                {remainingPositions} of {internship.totalPositions} remaining
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {remainingPositions !== null && (
                                <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
                                    <div className="flex justify-between items-center text-xs font-bold text-slate-600 mb-1.5">
                                        <span>Seats Filled</span>
                                        <span>{Math.round(((internship.currentRegistrations || 0) / internship.totalPositions) * 100)}%</span>
                                    </div>
                                    <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                        <div 
                                            className="bg-indigo-600 h-2 rounded-full" 
                                            style={{ width: `${Math.min(100, Math.round(((internship.currentRegistrations || 0) / internship.totalPositions) * 100))}%` }}
                                        ></div>
                                    </div>
                                </div>
                            )}

                            <div className="pt-4 border-t border-slate-100">
                                {isAvailable ? (
                                    <button
                                        onClick={handleApplyClick}
                                        className="w-full inline-flex items-center justify-center px-6 py-3.5 text-base font-bold rounded-xl text-white bg-indigo-600 hover:bg-indigo-700 hover-glow transition-all active:scale-[0.98] shadow-sm shadow-indigo-100"
                                    >
                                        Apply for Program
                                    </button>
                                ) : (
                                    <button
                                        disabled
                                        className="w-full inline-flex items-center justify-center px-6 py-3.5 text-base font-bold rounded-xl bg-slate-200 text-slate-400 cursor-not-allowed border border-slate-300/40"
                                    >
                                        {(!internship.isAccepting) ? 'Applications Closed' : 'All Seats Filled'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InternshipDetails;